import {
	EnvironmentModel,
	InstanceOperatorEnvironmentsPageViewModel
} from "client/Api/InstanceOperatorPage/instanceOperatorPage";
import {
	Anchor,
	TableView,
	TableViewNoResultConfig
} from "client/Components";
import { contains, normalizeForSearch } from "client/Data/searching";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import {
	createLocalDataSource,
	useFilterHandler,
	useSortHandler
} from "client/Hooks";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { createRoot } from "react-dom/client";
import { useClearEnvironmentCacheDialog } from "./ClearEnvironmentCacheDialog";
import styles from "./Environments.module.less";
import { localize } from "client_legacy/ndaw/culture";
import { InstanceOperatorLayout } from "./InstanceOperatorLayout";
import { ensureNotNil } from "client/Misc/utils";

type EnvironmentsProps = {
	environments: readonly EnvironmentModel[];
	canClearCache: boolean;
};

const Environments = (props: EnvironmentsProps): ReactElement => {
	const {
		environments,
		canClearCache
	} = props;

	const [sortingState, setSortingState] = useState<DataSourceSortableState<EnvironmentModel>>({
		direction: DataSourceSortDirection.Ascending,
		propertyName: "name"
	});

	const [filterState, setFilterState] = useState<FilterState>(DEFAULT_FILTERS);

	const [clearEnvironmentCacheDialog, openClearEnvironmentCacheDialog] = useClearEnvironmentCacheDialog();

	const clearCacheClickHandler = useCallback(
		(environment: EnvironmentModel): void => {
			openClearEnvironmentCacheDialog({
				environmentId: environment.id,
				environmentName: environment.name
			});
		},
		[openClearEnvironmentCacheDialog]
	);

	const { currentItems } = useDataSource(
		environments,
		{
			filtering: filterState,
			sorting: sortingState
		}
	);

	const {
		sortingFor
	} = useSortHandler(
		sortingState,
		setSortingState,
		false
	);

	const {
		filteringFor
	} = useFilterHandler<FilterState>(filterState, setFilterState);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			return {
				icon: "gear",
				title: localize("_NoResults"),
				message: localize("_NoResultToShowYet")
			};
		},
		[]
	);

	return (
		<>
			<InstanceOperatorLayout>
				<div className={styles.root}>
					<h3 className={styles.title}>
						Environments
					</h3>

					<TableView
						trackingId="environments"
						height="stretch"
						columns={[
							{
								title: "Name",
								width: "fill",
								minWidth: 200,
								trackingId: "name",
								sortable: sortingFor("name"),
								filterable: filteringFor("name")
							},
							{
								title: "Id",
								trackingId: "id",
								width: 350,
								sortable: sortingFor("id"),
								filterable: filteringFor("id")
							},
							{
								title: "Status",
								trackingId: "status",
								sortable: sortingFor("isActive")
							},
							{
								title: "Partner",
								trackingId: "partner",
								sortable: sortingFor("partnerName"),
								filterable: filteringFor("partner")
							},
							canClearCache && {
								title: "",
								trackingId: "clear-shared-cache"
							}
						]}
						items={currentItems}
						itemKey={getItemKey}
						hasZebraPrinting
						headerExtension={(
							<Anchor onClick={() => setFilterState(DEFAULT_FILTERS)}>
								Clear filters
							</Anchor>
						)}
						noResultConfig={noResultConfig}
						hasNoBottomDelimiter
					>
						{
							(x, itemKey): ReactElement => {
								return (
									<TableView.Row
										trackingId={`${itemKey}`}
									>
										<TableView.Cell>
											<span className={styles.selectableText}>
												{x.name}
											</span>
										</TableView.Cell>

										<TableView.Cell>
											<span className={styles.selectableText}>
												{x.id}
											</span>
										</TableView.Cell>

										<TableView.Cell>
											<span className={styles.selectableText}>
												{x.isActive ? "Active" : "Inactive"}
											</span>
										</TableView.Cell>

										<TableView.Cell>
											<span className={styles.selectableText}>
												{x.partnerName}
											</span>
										</TableView.Cell>

										{canClearCache && (
											<TableView.Cell>
												<Anchor onClick={() => clearCacheClickHandler(x)}>
													Clear cache
												</Anchor>
											</TableView.Cell>
										)}
									</TableView.Row>
								);
							}
						}
					</TableView>
				</div>
			</InstanceOperatorLayout>
			{clearEnvironmentCacheDialog}
		</>
	);
};
Environments.displayName = "Environments";

function getItemKey(item: EnvironmentModel): string {
	return item.id;
}

type FilterState = {
	name: string;
	id: string;
	partner: string;
};

const DEFAULT_FILTERS: FilterState = {
	name: "",
	id: "",
	partner: ""
};

function applyFilter(
	items: readonly EnvironmentModel[],
	filter: FilterState
): readonly EnvironmentModel[] {
	const normalizedName: string = normalizeForSearch(filter.name);
	const normalizedId: string = normalizeForSearch(filter.id);
	const normalizedPartner: string = normalizeForSearch(filter.partner);

	return items.filter(i =>
		(normalizedName === "" || contains(i.name, normalizedName)) &&
		(normalizedId === "" || contains(i.id, normalizedId)) &&
		(normalizedPartner === "" || contains(i.partnerName ?? "", normalizedPartner))
	);
}

const useDataSource = createLocalDataSource<EnvironmentModel, FilterState>()
	.setConfiguration({
		filtering: { applyFilter }
	})
	.getHook();

export function init(model: InstanceOperatorEnvironmentsPageViewModel): void {
	createRoot(
		ensureNotNil(
			document.getElementById("instance-operator-environments")
		)
	)
		.render(
			<Environments {...model} />

		);
}
